/**
 * The English name of the person a yahrzeit remembers.
 *
 * It is written as two halves — `firstName` and `lastName` — because that is
 * what the Add, Edit and public forms ask for, and what a design can print on a
 * line of its own via `{firstName}` / `{lastName}`. The record's `name` is the
 * two joined, derived server-side on every write, and it is what every surface
 * wanting the name in one piece reads: the list rows, the avatars, the upcoming
 * rail, the search, `{englishName}` in a design.
 *
 * These are the crossing points between the two shapes — the same arrangement
 * `yurtzeiten_submitters.ts` has for the people who put a yahrzeit in.
 */

/** A record's English name, however much of it the record carries. */
type YurtzeitenNameLike = {
  firstName?: unknown;
  lastName?: unknown;
  name?: unknown;
};

export interface YurtzeitenNameParts {
  firstName: string;
  lastName: string;
}

/** The two halves as one string, with no gap when either is empty. */
export const yurtzeitenFullName = (
  first: unknown,
  last: unknown
): string =>
  [first, last]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' ');

/**
 * A whole name back as its two halves: first word the given name, everything
 * after it the family name. The only split a single stored string supports.
 */
export const splitYurtzeitenFullName = (value: unknown): YurtzeitenNameParts => {
  const parts = String(value ?? '').trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
};

/**
 * The two halves a form opens on.
 *
 * The stored halves win when the record has them. A record written before the
 * name was split is migrated server-side, so this only has to fall back for one
 * that reaches the UI ahead of that: those are split out of the whole name they
 * already carry, which is what they already display.
 */
export const toYurtzeitenNameParts = (
  record: YurtzeitenNameLike | null | undefined
): YurtzeitenNameParts => {
  const firstName = String(record?.firstName ?? '').trim();
  const lastName = String(record?.lastName ?? '').trim();

  if (firstName || lastName) return { firstName, lastName };

  return splitYurtzeitenFullName(record?.name);
};

/**
 * A record's whole English name, from whichever shape it carries. Prefers the
 * halves so a record edited in this session reads back the name just typed,
 * even before the server's own joined value has been reloaded.
 */
export const yurtzeitenRecordName = (
  record: YurtzeitenNameLike | null | undefined
): string => {
  const joined = yurtzeitenFullName(record?.firstName, record?.lastName);
  return joined || String(record?.name ?? '').trim();
};
